import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { ChevronDown, ChevronRight, Check, Minus, X } from "lucide-react";
import { Box, Flex, Text, Theme } from "@radix-ui/themes";
import { EntityFieldTree } from "@/types/breeze/Entity";
import { formatColumnName } from "@/utils/text";

interface FieldSelectorProps {
  value?: string[];
  onChange?: (fields: string[]) => void;
  treeData: EntityFieldTree[];
  placeholder?: string;
}

// Custom CSS for theme-aware styling
const fieldSelectorStyles = `
  .field-selector-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 300px;
    gap: 8px;
    padding: 8px 12px;
    height: 32px;
    font-size: 12px;
    border: 1px solid var(--gray-6);
    border-radius: var(--radius-2);
    background: var(--color-panel-solid);
    color: var(--gray-12);
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .field-selector-trigger:hover {
    background: var(--gray-3);
  }

  .field-selector-trigger:focus {
    outline: none;
    border-color: var(--accent-8);
    box-shadow: 0 0 0 1px var(--accent-8);
  }

  .field-selector-content {
    z-index: 50;
    width: 300px;
    padding: 4px;
    background: var(--color-panel-solid);
    border-radius: var(--radius-3);
    box-shadow: var(--shadow-5);
    border: 1px solid var(--gray-6);
  }

  .field-selector-node {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    cursor: pointer;
    border-radius: var(--radius-2);
    transition: background-color 0.2s ease;
    user-select: none;
  }

  .field-selector-node:hover {
    background: var(--gray-3);
  }

  .field-selector-expand-button {
    padding: 2px;
    border-radius: var(--radius-2);
    transition: background-color 0.2s ease;
  }

  .field-selector-expand-button:hover {
    background: var(--gray-3);
  }

  .field-selector-clear-button {
    padding: 2px;
    border-radius: var(--radius-2);
    transition: background-color 0.2s ease;
  }

  .field-selector-clear-button:hover {
    background: var(--gray-3);
  }

  .field-selector-scrollbar {
    display: flex;
    user-select: none;
    touch-action: none;
    padding: 2px;
    background: var(--gray-2);
    transition: background-color 160ms ease-out;
    width: 10px;
  }

  .field-selector-scrollbar:hover {
    background: var(--gray-3);
  }

  .field-selector-thumb {
    flex: 1;
    background: var(--gray-6);
    border-radius: 10px;
    position: relative;
  }
`;

export function FieldSelector({
  value = [],
  onChange,
  treeData,
  placeholder = "Select Fields",
}: FieldSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(
    new Set(),
  );

  // Helper function to check if any descendants are selected
  const hasSelectedDescendants = (node: EntityFieldTree): boolean => {
    if (!node.children) return false;
    return node.children.some(
      (child) => value.includes(child.path) || hasSelectedDescendants(child),
    );
  };

  // Helper function to check selection state of a node
  const getNodeSelectionState = (
    node: EntityFieldTree,
  ): "none" | "partial" | "all" => {
    const isSelected = value.includes(node.path);
    const hasChildren = node.children && node.children.length > 0;

    if (isSelected) return "all";
    if (!hasChildren) return "none";

    return hasSelectedDescendants(node) ? "partial" : "none";
  };

  const toggleExpanded = (nodeValue: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeValue)) {
        next.delete(nodeValue);
      } else {
        next.add(nodeValue);
      }
      return next;
    });
  };

  const renderTreeNode = (node: EntityFieldTree, level = 0) => {
    const selectionState = getNodeSelectionState(node);
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.path);

    const handleSelect = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onChange) return;

      // Simple toggle of the current node only
      if (value.includes(node.path)) {
        onChange(value.filter((v) => v !== node.path));
      } else {
        onChange([...value, node.path]);
      }
    };

    return (
      <Box key={node.path} style={{ userSelect: "none" }}>
        <div
          className="field-selector-node"
          style={{ paddingLeft: `${level * 16}px` }}
          onClick={handleSelect}
        >
          {hasChildren ? (
            <button
              data-testid={`field-selector-expand-button-${node.path}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(node.path);
              }}
              className="field-selector-expand-button"
            >
              {isExpanded ? (
                <ChevronDown size={16} style={{ color: "var(--gray-12)" }} />
              ) : (
                <ChevronRight size={16} style={{ color: "var(--gray-12)" }} />
              )}
            </button>
          ) : (
            <Box style={{ width: "20px" }} />
          )}
          <Flex
            align="center"
            justify="between"
            flexGrow="1"
            style={{
              borderRadius: "var(--radius-2)",
              color:
                selectionState === "all" || selectionState === "partial"
                  ? "var(--accent-9)"
                  : "var(--gray-12)",
            }}
          >
            <Text size="2">
              {node.definition?.label || formatColumnName(node.name)}
            </Text>
            {selectionState === "all" && <Check size={16} />}
            {selectionState === "partial" && <Minus size={16} />}
          </Flex>
        </div>
        {hasChildren && isExpanded && (
          <Box ml="2">
            {node.children?.map((child) => renderTreeNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange([]);
    }
  };

  return (
    <>
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: fieldSelectorStyles }} />

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button className="field-selector-trigger">
            <Text
              size="2"
              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {value.length > 0
                ? `${value.length} field${
                    value.length === 1 ? "" : "s"
                  } selected`
                : placeholder}
            </Text>
            <Flex align="center" gap="1">
              {value.length > 0 && (
                <button
                  onClick={handleClear}
                  className="field-selector-clear-button"
                >
                  <X size={16} style={{ color: "var(--gray-12)" }} />
                </button>
              )}
              <ChevronDown
                size={16}
                style={{
                  color: "var(--gray-12)",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </Flex>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Theme>
            <Popover.Content
              className="field-selector-content"
              align="start"
              sideOffset={4}
            >
              <ScrollArea.Root style={{ height: "400px", overflow: "hidden" }}>
                <ScrollArea.Viewport style={{ height: "100%", width: "100%" }}>
                  {treeData.map((node) => renderTreeNode(node))}
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar
                  className="field-selector-scrollbar"
                  orientation="vertical"
                >
                  <ScrollArea.Thumb className="field-selector-thumb" />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </Popover.Content>
          </Theme>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}
